/**
 * @fileoverview Reusable Data Table Component
 * @task B.INV.2 - Common UI components for data display
 *
 * Features:
 * - Column sorting (ascending/descending)
 * - Pagination with page size selector
 * - Loading and empty states
 * - Responsive design
 * - Row actions support
 */

import { useState, useCallback, useMemo } from 'react';
import { cn } from '@lib/utils';
import { Button } from '@components/ui/button';
import { useMarket } from '@contexts/MarketContext';

// ============================================================================
// Types
// ============================================================================

export interface Column<T> {
  key: string;
  header: string;
  sortable?: boolean;
  width?: string;
  align?: 'left' | 'center' | 'right';
  render?: (item: T, index: number) => React.ReactNode;
  className?: string;
}

export interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
}

export interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  keyField: keyof T;
  isLoading?: boolean;
  emptyMessage?: string;
  emptyIcon?: React.ReactNode;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSort?: (column: string, order: 'asc' | 'desc') => void;
  pagination?: PaginationProps;
  rowClassName?: (item: T, index: number) => string;
  onRowClick?: (item: T) => void;
  className?: string;
  stickyHeader?: boolean;
}

// ============================================================================
// Pagination Component
// ============================================================================

function Pagination({
  page,
  pageSize,
  total,
  totalPages,
  onPageChange,
  onPageSizeChange,
}: PaginationProps): JSX.Element {
  const { isBrazil } = useMarket();
  const startItem = Math.min((page - 1) * pageSize + 1, total);
  const endItem = Math.min(page * pageSize, total);

  const pageSizeOptions = [5, 10, 20, 50];

  // Generate page numbers to display
  const pageNumbers = useMemo(() => {
    const pages: (number | 'ellipsis')[] = [];
    const showPages = 5; // Number of page buttons to show

    if (totalPages <= showPages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (page > 3) {
        pages.push('ellipsis');
      }

      // Show pages around current page
      const start = Math.max(2, page - 1);
      const end = Math.min(totalPages - 1, page + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (page < totalPages - 2) {
        pages.push('ellipsis');
      }

      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  }, [page, totalPages]);

  return (
    <div className="flex flex-col items-center justify-between gap-3 border-t bg-card px-3 sm:px-4 py-3 sm:flex-row">
      <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-4">
        {/* Simplified mobile text */}
        <p className="text-xs sm:text-sm text-muted-foreground">
          {isBrazil ? (
            <>
              <span className="font-medium">{startItem}</span>-<span className="font-medium">{endItem}</span>
              <span className="hidden sm:inline"> de </span>
              <span className="sm:hidden">/</span>
              <span className="font-medium">{total}</span>
            </>
          ) : (
            <>
              <span className="font-medium">{startItem}</span>-<span className="font-medium">{endItem}</span>
              <span className="hidden sm:inline"> of </span>
              <span className="sm:hidden">/</span>
              <span className="font-medium">{total}</span>
            </>
          )}
        </p>
        {onPageSizeChange && (
          <div className="hidden sm:flex items-center gap-2">
            <label htmlFor="page-size" className="text-sm text-muted-foreground">
              {isBrazil ? 'Exibir:' : 'Show:'}
            </label>
            <select
              id="page-size"
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="h-9 rounded-md border bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Mobile-friendly pagination buttons with larger touch targets */}
      <div className="flex items-center gap-1 sm:gap-1">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className="h-10 w-10 sm:h-9 sm:w-9 p-0"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="sr-only">{isBrazil ? 'Página anterior' : 'Previous page'}</span>
        </Button>

        {/* Hide middle page numbers on mobile, show only current */}
        <span className="sm:hidden px-3 text-sm font-medium">
          {page}/{totalPages}
        </span>

        {/* Full page numbers on desktop */}
        <div className="hidden sm:flex items-center gap-1">
          {pageNumbers.map((pageNum, index) =>
            pageNum === 'ellipsis' ? (
              <span key={`ellipsis-${index}`} className="px-2 text-muted-foreground">
                ...
              </span>
            ) : (
              <Button
                key={pageNum}
                variant={pageNum === page ? 'default' : 'outline'}
                size="sm"
                onClick={() => onPageChange(pageNum)}
                className="h-9 w-9 p-0"
              >
                {pageNum}
              </Button>
            )
          )}
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          className="h-10 w-10 sm:h-9 sm:w-9 p-0"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="sr-only">{isBrazil ? 'Próxima página' : 'Next page'}</span>
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// Sort Icon Component
// ============================================================================

function SortIcon({
  active,
  order,
}: {
  active: boolean;
  order: 'asc' | 'desc';
}): JSX.Element {
  if (!active) {
    return (
      <svg
        className="h-4 w-4 text-muted-foreground/50"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
      </svg>
    );
  }

  return order === 'asc' ? (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
    </svg>
  ) : (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

// ============================================================================
// Loading Skeleton
// ============================================================================

function TableSkeleton({ columns }: { columns: number }): JSX.Element {
  return (
    <>
      {[...Array(5)].map((_, rowIndex) => (
        <tr key={rowIndex} className="border-b">
          {[...Array(columns)].map((__, colIndex) => (
            <td key={colIndex} className="p-4">
              <div className="h-4 w-full animate-pulse rounded bg-muted" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

// ============================================================================
// Empty State
// ============================================================================

function EmptyState({
  message,
  icon,
}: {
  message: string;
  icon?: React.ReactNode;
}): JSX.Element {
  return (
    <tr>
      <td colSpan={100} className="p-12">
        <div className="flex flex-col items-center justify-center text-center">
          {icon ?? (
            <svg
              className="mb-4 h-12 w-12 text-muted-foreground/50"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          )}
          <p className="text-muted-foreground">{message}</p>
        </div>
      </td>
    </tr>
  );
}

// ============================================================================
// Data Table Component
// ============================================================================

/**
 * DataTable Component
 * A reusable, sortable, paginated data table with loading and empty states
 */
export function DataTable<T>({
  data,
  columns,
  keyField,
  isLoading = false,
  emptyMessage,
  emptyIcon,
  sortBy,
  sortOrder = 'asc',
  onSort,
  pagination,
  rowClassName,
  onRowClick,
  className,
  stickyHeader = false,
}: DataTableProps<T>): JSX.Element {
  const { isBrazil } = useMarket();
  const [internalSortBy, setInternalSortBy] = useState<string | undefined>(sortBy);
  const [internalSortOrder, setInternalSortOrder] = useState<'asc' | 'desc'>(sortOrder);
  const defaultEmptyMessage = isBrazil ? 'Nenhum dado disponível' : 'No data available';
  const finalEmptyMessage = emptyMessage ?? defaultEmptyMessage;

  const handleSort = useCallback(
    (column: string) => {
      const newOrder =
        column === (sortBy ?? internalSortBy) && (sortOrder ?? internalSortOrder) === 'asc'
          ? 'desc'
          : 'asc';

      if (onSort) {
        onSort(column, newOrder);
      } else {
        setInternalSortBy(column);
        setInternalSortOrder(newOrder);
      }
    },
    [sortBy, sortOrder, internalSortBy, internalSortOrder, onSort]
  );

  const currentSortBy = sortBy ?? internalSortBy;
  const currentSortOrder = sortOrder ?? internalSortOrder;

  const alignmentClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };

  return (
    <div className={cn('overflow-hidden rounded-lg border bg-card', className)}>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead
            className={cn(
              'border-b bg-muted/50',
              stickyHeader && 'sticky top-0 z-10'
            )}
          >
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={cn(
                    'whitespace-nowrap px-4 py-3 text-sm font-semibold',
                    alignmentClasses[column.align ?? 'left'],
                    column.sortable && 'cursor-pointer select-none hover:bg-muted',
                    column.className
                  )}
                  style={{ width: column.width }}
                  onClick={() => column.sortable && handleSort(column.key)}
                >
                  <div
                    className={cn(
                      'flex items-center gap-2',
                      column.align === 'right' && 'justify-end',
                      column.align === 'center' && 'justify-center'
                    )}
                  >
                    {column.header}
                    {column.sortable && (
                      <SortIcon
                        active={currentSortBy === column.key}
                        order={currentSortOrder}
                      />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <TableSkeleton columns={columns.length} />
            ) : !data || data.length === 0 ? (
              <EmptyState message={finalEmptyMessage} icon={emptyIcon} />
            ) : (
              data.map((item, index) => (
                <tr
                  key={String(item[keyField])}
                  className={cn(
                    'border-b transition-colors last:border-0 hover:bg-muted/50',
                    onRowClick && 'cursor-pointer',
                    rowClassName?.(item, index)
                  )}
                  onClick={() => onRowClick?.(item)}
                >
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={cn(
                        'whitespace-nowrap px-4 py-3 text-sm',
                        alignmentClasses[column.align ?? 'left'],
                        column.className
                      )}
                    >
                      {column.render
                        ? column.render(item, index)
                        : String((item as Record<string, unknown>)[column.key] ?? '')}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination && !isLoading && data && data.length > 0 && (
        <Pagination {...pagination} />
      )}
    </div>
  );
}

export default DataTable;
